import React from "react";
import { string, bool } from "prop-types";
import { motion } from "framer-motion";
import { useTranslation } from "@lib/i18n";
import { useRouter } from "next/router";

import ProgressBar from "../progressBar/ProgressBar";
import ContentContainer from "./ContentContainer";
import ThankYouSectionBanner from "../ThankYouSectionBanner";
import Typography from "@components/shared/Typography";

import { ROUTES } from "@shared/routing/routes";
import { useConfigurationPathname } from "@hooks/useConfigurationPathname";

import gtm from "@gtm/core";
import {
  finalTestDriveRedirectEvent,
  testDriveConfirmationUpdateUrlEvent,
} from "@gtm/events/testdrive";

import { useTestDrivePointSelector } from "@redux/reducers/testDrive";
import { useEffect } from "react";

const FinalStep = ({ affiliation, gitId, testDriveType, isSSRRendered }) => {
  const { t } = useTranslation();
  const { zipcode } = useTestDrivePointSelector();
  const router = useRouter();
  const configurationPathname = useConfigurationPathname();

  useEffect(() => {
    gtm.fire(
      testDriveConfirmationUpdateUrlEvent({
        type: testDriveType,
        gitId,
        postalCode: zipcode,
        pageTitle: router.asPath,
        affiliation,
        isSSRRendered,
      })
    );
  }, []);

  const handleThankYouButtonClick = () => {
    gtm.fire(finalTestDriveRedirectEvent());

    router.push(ROUTES.CONFIG, configurationPathname);
  };

  return (
    <>
      <section className="flex justify-start items-stretch w-full">
        <ProgressBar isComplete isFinalStep />
        <ContentContainer title={t("testDrive.merciSection.title")}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            <h2
              className="text-2xl text-primaryDark uppercase font-semibold mb-2"
              data-id="test-drive-thank-you"
            >
              {t("testDrive.merciSection.headline")}
            </h2>
            <Typography className="text-lg">
              {t("testDrive.merciSection.text1")}
            </Typography>
          </motion.div>
        </ContentContainer>
      </section>
      <motion.div
        animate={{ x: 0 }}
        className="sm:ml-16"
        initial={{ x: "100%" }}
        transition={{ delay: 1.4, duration: 0.3 }}
      >
        <ThankYouSectionBanner
          onThankYouButtonClick={handleThankYouButtonClick}
        />
      </motion.div>
    </>
  );
};

FinalStep.propTypes = {
  affiliation: string,
  gitId: string,
  testDriveType: string.isRequired,
  isSSRRendered: bool,
};

export default FinalStep;
